import React, { useState, useEffect } from "react";
import Image from "next/image";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import Spinner from "react-bootstrap/Spinner";
import { setCookies } from "cookies-next";
import httpClient from "@/api/http-client";
import endpoints from "@/api/endpoints";
import { useAppDispatch } from "@/store";
import { fetchAuthUser } from "@/store/slices/auth";
import { fetchCartItems } from "@/store/slices/cart";
import { fetchWishlistItems } from "@/store/slices/wishlist";
import { initTryOnTracking } from "@/lib/tryOnTracking";
import { unbxdCusEngTrackEvent, unbxdCusEngTrackUser } from "@/lib/unbxd";
import moment from "moment-timezone";
import styles from "../../styles/shap_of_you.module.scss";
import WelcomeScreenImg from "../../assets/images/WelcomeScreen_dek.png";
import SOYLockUp from "../../assets/images/SOYLockUp.png";
import { VerifyOtpScreenProps } from "./types";

const RESEND_OTP_INTERVAL_SECONDS = 30;

const VerifyOtpScreen: React.FC<VerifyOtpScreenProps> = ({
  navigateToStep,
  authData,
  onAuthComplete,
  logStep,
}) => {
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);
  const [sendOtpLoading, setSendOtpLoading] = useState(false);
  const [resendTimeout, setResendTimeout] = useState(
    RESEND_OTP_INTERVAL_SECONDS
  );
  const [otpValues, setOtpValues] = useState(["", "", "", "", "", ""]);
  const [verificationStatus, setVerificationStatus] = useState<
    "idle" | "verifying" | "verified" | "error"
  >("idle");
  const [accessToken, setAccessToken] = useState<string | null>(null);

  const currentDate = moment().format("DD-MM-YYYY");

  const isOtpComplete = otpValues.every((val) => val !== "");

  // Countdown timer for resend OTP
  useEffect(() => {
    if (resendTimeout > 0) {
      const timer = setInterval(() => {
        setResendTimeout(resendTimeout - 1);
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [resendTimeout]);

  // OTP Formik
  const otpFormik = useFormik({
    initialValues: {
      otp: "",
    },
    validationSchema: Yup.object({
      otp: Yup.string()
        .required("OTP is required")
        .min(6, "Must be exactly 6 digits")
        .max(6, "Must be exactly 6 digits")
        .matches(/^\d{6}$/, "OTP must be 6 digits"),
    }),
    onSubmit: () => {}, // Not used anymore - using handleVerifyOtp and handleSubmit instead
  });

  // Step 1: Verify OTP (without navigating)
  const handleVerifyOtp = async () => {
    setVerificationStatus("verifying");
    setLoading(true);

    try {
      const otpString = otpValues.join("");

      if (authData.authFlow === "login") {
        // Login OTP verification
        const apiEndpoint = endpoints.auth.loginVerifyOtp();
        const requestPayload = {
          mobile: authData.mobileNumber,
          otp: otpString,
          otp_uuid: authData.otpUid,
        };

        const { data } = await httpClient.post(apiEndpoint, requestPayload);

        // Log successful login (non-blocking)
        if (logStep) {
          logStep("signup_login_success", {
            api_endpoint: apiEndpoint,
            request_payload: { ...requestPayload, otp: "******" }, // Sanitized
            response_status: 200,
            response_payload: { access_token: "[token]" }, // Sanitized
            success_message: "Login successful",
          });
        }

        // Store access token and set verified status
        setAccessToken(data?.access_token);
        setVerificationStatus("verified");

        // Automatically proceed to login after successful verification
        await handleAutoLogin(data?.access_token);
      } else {
        // Register OTP verification
        const apiEndpoint = endpoints.auth.signup();
        const requestPayload = {
          name: `${authData.registerData.firstName} ${authData.registerData.lastName}`,
          mobile: authData.mobileNumber,
          email: authData.registerData.email,
          pincode: authData.registerData.pincode,
          otp: otpString,
          otp_uuid: authData.otpUid,
          source: "shape_of_you",
        };

        const { data } = await httpClient.post(apiEndpoint, requestPayload);

        // Log successful registration (non-blocking)
        if (logStep) {
          logStep("signup_register_success", {
            api_endpoint: apiEndpoint,
            request_payload: { ...requestPayload, otp: "******" }, // Sanitized
            response_status: 200,
            response_payload: { access_token: "[token]" }, // Sanitized
            success_message: "Registration successful",
          });
        }

        // Track registration events
        if (window.Moengage) {
          window.Moengage.track_event("Registration Completed", {
            mobile: authData.mobileNumber,
            platform: "website",
          });
        }

        if (window.smartech) {
          unbxdCusEngTrackUser({
            "pk^mobile": authData.mobileNumber,
            NAME: `${authData.registerData.firstName} ${authData.registerData.lastName}`,
            email: authData.registerData.email,
            PINCODE: authData.registerData.pincode,
            SENCO_USER: "yes",
            REGISTRATION_DATE_SENCO: currentDate,
          });
          window.smartech("identify", authData.mobileNumber);
          setTimeout(() => {
            unbxdCusEngTrackEvent("Registration", {
              source: "web",
              platform: "senco",
            });
          }, 200);
        }

        // Store access token and set verified status
        setAccessToken(data.access_token);
        setVerificationStatus("verified");

        // Automatically proceed to login after successful verification
        await handleAutoLogin(data.access_token);
      }
    } catch (error: any) {
      // Log API error (non-blocking)
      if (logStep) {
        const stepName =
          authData.authFlow === "login"
            ? "signup_login_success"
            : "signup_register_success";
        logStep(stepName, {
          api_endpoint:
            authData.authFlow === "login"
              ? endpoints.auth.loginVerifyOtp()
              : endpoints.auth.signup(),
          response_status: error.response?.status || 500,
          error_message:
            error.response?.data?.message || "OTP verification failed",
          error_code: error.response?.status?.toString() || "500",
        });
      }

      // Check if error is "OTP does not match"
      if (error.response?.status === 400) {
        setVerificationStatus("error");
      } else {
        setVerificationStatus("error");
        if (error.response?.data?.message) {
          toast(error.response.data.message, {
            type: "error",
            toastId: "auth-otp-error",
          });
        } else {
          toast("Something went wrong! Try again!", {
            type: "error",
            toastId: "auth-otp-error",
          });
        }
      }
    }
    setLoading(false);
  };

  // Auto-login after successful registration/login
  const handleAutoLogin = async (token: string) => {
    try {
      setCookies("token", token, { path: "/" });
      const { payload: user }: any = await dispatch(fetchAuthUser());

      toast(`Welcome ${user.name}`, {
        type: "success",
        toastId: "auth-login-success",
      });

      if (window.Moengage) {
        window.Moengage.track_event("Login", {
          mobile: user.mobile,
        });
        window.Moengage.add_first_name(user.name);
        window.Moengage.add_mobile(user.mobile);
        window.Moengage.add_unique_user_id(user.uuid);
        if (user.email) {
          window.Moengage.add_email(user.email);
        }
      }

      if (window.smartech) {
        unbxdCusEngTrackUser({
          "pk^mobile": user?.mobile,
          SENCO_USER: "yes",
        });
        window.smartech("identify", user?.mobile);
        unbxdCusEngTrackEvent("Sign in ", {
          source: "web",
          platform: "senco",
        });
      }

      dispatch(fetchCartItems());
      dispatch(fetchWishlistItems(user.id));
      initTryOnTracking(user.id);

      // Check if there's a pending consent acceptance from T&C screen
      const pendingConsent = localStorage.getItem("pendingConsentAcceptance");
      if (pendingConsent === "true") {
        try {
          // Call updateConsentForm API
          await httpClient.post(endpoints.auth.updateConsentForm(), {
            status: "accept",
          });

          // Clear the localStorage flag
          localStorage.removeItem("pendingConsentAcceptance");

          console.log("Pending consent accepted successfully");
        } catch (consentError) {
          console.error("Error updating pending consent:", consentError);
          // Don't block the login flow if consent update fails
        }
      }

      // Call parent completion handler or fallback to navigation
      if (onAuthComplete) {
        onAuthComplete();
      } else {
        // Fallback: Navigate to product view after successful login
        navigateToStep("productView");
      }
    } catch (error: any) {
      console.error("Auto-login error:", error);
      toast("Login successful but failed to fetch user data", {
        type: "warning",
        toastId: "auth-warning",
      });
    }
  };

  const handleOtpChange = (index: number, value: string) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const newOtpValues = [...otpValues];
      newOtpValues[index] = value;
      setOtpValues(newOtpValues);

      // Reset verification status when user modifies OTP
      if (verificationStatus === "error" || verificationStatus === "verified") {
        setVerificationStatus("idle");
      }

      // Update formik value
      const otpString = newOtpValues.join("");
      otpFormik.setFieldValue("otp", otpString, false);

      // Auto-focus next input
      if (value && index < 5) {
        const nextInput = document.getElementById(`otp-${index + 1}`);
        nextInput?.focus();
      }
    }
  };

  const handleOtpKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === "Backspace" && !otpValues[index] && index > 0) {
      const prevInput = document.getElementById(`otp-${index - 1}`);
      prevInput?.focus();
    }
  };

  // Handle resend OTP
  const handleResendOtp = async () => {
    if (resendTimeout > 0) return;

    setSendOtpLoading(true);
    try {
      let apiEndpoint: string;
      let requestPayload: any;

      if (authData.authFlow === "login") {
        apiEndpoint = endpoints.auth.loginSendOtp();
        requestPayload = {
          mobile: authData.mobileNumber,
        };
      } else {
        // Register flow - use the same endpoint as SignupScreen
        apiEndpoint = endpoints.auth.sendOtp();
        requestPayload = {
          mobile: authData.mobileNumber,
          email: authData.registerData.email,
          pincode: authData.registerData.pincode,
        };
      }

      const { data } = await httpClient.post(apiEndpoint, requestPayload);

      toast(data.message || "OTP resent successfully", {
        type: "success",
        toastId: "auth-otp-resend-success",
      });

      // Reset timer
      setResendTimeout(RESEND_OTP_INTERVAL_SECONDS);

      // Note: We don't update authData.otpUid here as it's passed as prop
      // If needed, parent component should handle updating the otpUid
    } catch (error: any) {
      if (error.response?.data?.message) {
        toast(error.response.data.message, {
          type: "error",
          toastId: "auth-otp-resend-error",
        });
      } else {
        toast("Failed to resend OTP. Please try again!", {
          type: "error",
          toastId: "auth-otp-resend-error",
        });
      }
    }
    setSendOtpLoading(false);
  };

  return (
    <div className="d-none d-md-block w-100">
      <div className={styles.otp_face_desktop}>
        <div className={styles.bgImage}>
          <Image src={WelcomeScreenImg} alt="Welcome Screen" />
        </div>
        <div className={styles.shapeOfYou}>
          <div className="container">
            <div className="row">
              <div className="col-md-7">
                <div className={styles.content}>
                  <div className={styles.title}>
                    <Image src={SOYLockUp} alt="Shape of You Logo" />
                  </div>
                </div>
              </div>
              <div className="col-md-5">
                <div className={styles.Login_container_form}>
                  <div className={styles.card}>
                    <h1 className={styles.title_m}>Verify OTP</h1>
                    <p className={styles.subtitle}>
                      OTP has been sent to your phone number
                    </p>

                    <div className={styles.otpContainer}>
                      {[0, 1, 2, 3, 4, 5].map((index) => (
                        <input
                          key={index}
                          id={`otp-${index}`}
                          type="password"
                          maxLength={1}
                          className={styles.otpInput}
                          value={otpValues[index]}
                          onChange={(e) =>
                            handleOtpChange(index, e.target.value)
                          }
                          onKeyDown={(e) => handleOtpKeyDown(index, e)}
                          style={
                            verificationStatus === "error"
                              ? {
                                  border: "2px solid #dc3545",
                                  backgroundColor: "#ffe6e6",
                                }
                              : {}
                          }
                        />
                      ))}
                    </div>

                    {/* Error message for incorrect OTP */}
                    {verificationStatus === "error" && (
                      <p
                        className="text-danger"
                        style={{
                          display: "block",
                          textAlign: "left",
                        }}
                      >
                        Error! OTP incorrect
                      </p>
                    )}

                    <p className={styles.timer}>
                      (OTP Timer{" "}
                      {resendTimeout > 0 ? `${resendTimeout}s` : "over"})
                    </p>

                    <div className={styles.resendRow}>
                      <span>Didn&apos;t get an OTP?</span>
                      {resendTimeout > 0 ? (
                        <span style={{ color: "#ccc", cursor: "not-allowed" }}>
                          Resend OTP
                        </span>
                      ) : (
                        <button
                          className={styles.resendBtn}
                          onClick={handleResendOtp}
                          disabled={sendOtpLoading}
                          style={{ color: "#c9302c" }}
                        >
                          {sendOtpLoading ? "Sending..." : "Resend OTP"}
                        </button>
                      )}
                    </div>

                    <div
                      className={styles.editNumber}
                      onClick={() => navigateToStep("login")}
                      style={{ cursor: "pointer" }}
                    >
                      Edit Phone Number{" "}
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 36 36"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M24.6374 6.90763L26.1223 5.42265C27.3526 4.19245 29.3472 4.19245 30.5773 5.42265C31.8075 6.65286 31.8075 8.64739 30.5773 9.8776L29.0923 11.3626M24.6374 6.90763L14.6484 16.8966C13.8871 17.658 13.3471 18.6117 13.086 19.6561L12 24L16.3438 22.914C17.3883 22.653 18.342 22.1128 19.1034 21.3516L29.0923 11.3626M24.6374 6.90763L29.0923 11.3626"
                          stroke="#262121"
                          strokeWidth="3"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M28.4999 20.25C28.4999 25.1812 28.4998 27.6468 27.138 29.3064C26.8887 29.6101 26.6102 29.8887 26.3063 30.138C24.6468 31.5 22.1811 31.5 17.2498 31.5H16.5C10.8431 31.5 8.01474 31.5 6.25738 29.7426C4.50004 27.9853 4.5 25.1568 4.5 19.5V18.75C4.5 13.8188 4.5 11.3532 5.86191 9.69366C6.11125 9.38985 6.38985 9.11125 6.69366 8.86191C8.35318 7.5 10.8188 7.5 15.75 7.5"
                          stroke="#262121"
                          strokeWidth="3"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>

                    <button
                      className={styles.submitBtn}
                      onClick={(e) => {
                        e.preventDefault();
                        if (isOtpComplete && !loading) {
                          handleVerifyOtp();
                        }
                      }}
                      disabled={loading || !isOtpComplete}
                      style={
                        loading || !isOtpComplete
                          ? {
                              background: "#d3d3d3",
                              color: "#888",
                              cursor: "not-allowed",
                              opacity: 0.6,
                            }
                          : {
                              background: "#C9302C",
                              color: "white",
                              cursor: "pointer",
                            }
                      }
                    >
                      {loading ? "Submitting..." : "Submit"}{" "}
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 36 36"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M2.99972 19.4999L2.99951 16.5001H27.2574L21.3328 10.5754L23.4541 8.4541L33.0001 18.0001L23.4541 27.5461L21.3328 25.4246L27.2575 19.5001L2.99972 19.4999Z"
                          fill="currentcolor"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyOtpScreen;
